import type { HttpOptions } from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  split
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { select } from '@ps/web-core-store';
import { canUseDOM, getWindow, notEmpty } from '@ps/web-core-utils';
import { createClient } from 'graphql-ws';
import React, { useMemo } from 'react';

import store from '../store';

import { useApolloServerContext } from './ApolloServerProvider';

interface Props {
  httpLinkUri: HttpOptions['uri'];
  wsLinkUrl?: string;
  httpHeaders?: Record<string, unknown>;
  useIdpAuth?: boolean;
  ssrMode?: boolean;
}

const getToken = () => {
  const { getState } = store;
  const rootState = getState();
  const { idpToken } = select.session.get(rootState);
  if (!idpToken) {
    const storedData = sessionStorage.getItem('oauthTokens');
    const parsedData = storedData ? JSON.parse(storedData) : {};
    return parsedData.access_token;
  }
  return idpToken;
};

const getApolloClient = ({
  httpLinkUri,
  wsLinkUrl,
  httpHeaders = {},
  useIdpAuth = false,
  ssrMode = false
}: Props) => {
  const apolloInitialState = getWindow()?.__APOLLO_STATE__;
  const retryLink = new RetryLink({
    delay: { initial: 5000 }
  });
  const shouldRestoreState = canUseDOM && ssrMode && notEmpty(apolloInitialState);

  let link = createHttpLink({
    uri: httpLinkUri,
    fetchOptions: { cache: ssrMode ? 'no-store' : 'default' }
  });

  if (canUseDOM) delete window.__APOLLO_STATE__;

  if (useIdpAuth) {
    const authLink = setContext((_, { headers }) => ({
      headers: { ...headers, ...httpHeaders, authorization: `Bearer ${getToken()}` }
    }));
    link = authLink.concat(link);
  }

  if (canUseDOM && wsLinkUrl) {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsLinkUrl,
        keepAlive: 40000,
        connectionParams: async () => (useIdpAuth ? { authorization: `Bearer ${getToken()}` } : {})
      })
    );

    link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      link
    );
  }

  const links = ApolloLink.from([retryLink, link]);

  return new ApolloClient({
    link: links,
    cache: new InMemoryCache().restore(shouldRestoreState ? apolloInitialState : {}),
    ssrMode: !canUseDOM && ssrMode,
    ssrForceFetchDelay: 100
  });
};

const ApolloClientProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  httpLinkUri,
  wsLinkUrl,
  httpHeaders,
  useIdpAuth,
  ssrMode
}) => {
  const apolloServerContext = useApolloServerContext();
  const client = useMemo(
    () => getApolloClient({ httpLinkUri, wsLinkUrl, httpHeaders, useIdpAuth, ssrMode }),
    [httpLinkUri, wsLinkUrl, httpHeaders, useIdpAuth, ssrMode]
  );
  ssrMode && apolloServerContext.setClient?.(client);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { ApolloClientProvider as default, getApolloClient };
